import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';

import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import {
  SESSION_STORAGE,
  useSessionStorage,
} from '@sorare/core/src/hooks/useSessionStorage';

import type {
  claimInteruptedCardsFromPackMutation,
  claimInteruptedCardsFromPackMutationVariables,
} from './__generated__/usePackOpeningInterruptedSnackbar.graphql';

const CLAIM_INTERUPTED_CARDS_FROM_PACK_MUTATION = gql`
  mutation claimInteruptedCardsFromPackMutation(
    $input: claimCardsFromPackInput!
  ) {
    claimCardsFromPack(input: $input) {
      currentUser {
        slug
        claimablePacks(sport: FOOTBALL) {
          id
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  claimInteruptedCardsFromPackMutation,
  claimInteruptedCardsFromPackMutationVariables
>;

export const usePackOpeningInterruptedSnackbar = () => {
  const { currentUser } = useCurrentUserContext();
  const [ongoingPackOpeningUserSlug, setOngoingPackOpeningUserSlug] =
    useSessionStorage(SESSION_STORAGE.ongoingPackOpeningUserSlug);
  const [initialPackOpeningInterruptedSlug] = useState(
    ongoingPackOpeningUserSlug
  );

  const { showNotification } = useSnackNotificationContext();

  const [claimInteruptedCardsFromPack] = useMutation(
    CLAIM_INTERUPTED_CARDS_FROM_PACK_MUTATION
  );

  useEffect(() => {
    if (currentUser?.claimablePacks.length) {
      claimInteruptedCardsFromPack({
        variables: {
          input: {
            packId: currentUser.claimablePacks[0].id,
            chosenCardSlugs: currentUser.claimablePacks[0].cards.map(
              card => card.slug
            ),
          },
        },
      });
    }
  }, [currentUser?.claimablePacks, claimInteruptedCardsFromPack]);

  const shouldDisplay =
    initialPackOpeningInterruptedSlug === currentUser?.slug ||
    currentUser?.claimablePacks.length;

  useEffect(() => {
    if (shouldDisplay) {
      setTimeout(() => {
        showNotification('packOpeningInterrupted');
        setOngoingPackOpeningUserSlug(null);
      }, 2000);
    }
  }, [shouldDisplay, showNotification, setOngoingPackOpeningUserSlug]);
  return null;
};
